import { useCallback, useState } from "react";

import { customToast } from "../../../../components/notify";
import { FilePrefix } from "../../types";
import { useUploadFileMutation } from "../services/kioskApiSlice";

interface FileUploadTask {
    path: string;
    file: File;
}

const useBatchUploadFiles = () => {
    const [uploadFile] = useUploadFileMutation();
    const [isUploading, setIsUploading] = useState(false);

    const extractFileObjects = useCallback((data: unknown, currentPath = ""): FileUploadTask[] => {
        const files: FileUploadTask[] = [];

        if (data instanceof File) {
            return [{ path: currentPath, file: data }];
        }

        if (Array.isArray(data)) {
            data.forEach((item, index) => {
                const path = currentPath ? `${currentPath}[${index}]` : `${index}`;
                files.push(...extractFileObjects(item, path));
            });
        } else if (data && typeof data === "object") {
            Object.entries(data).forEach(([key, value]) => {
                const path = currentPath ? `${currentPath}.${key}` : key;
                files.push(...extractFileObjects(value, path));
            });
        }

        return files;
    }, []);

    const setValueByPath = useCallback((obj: Record<string, unknown>, path: string, value: unknown) => {
        const keys = path.split(/[.[\]]+/).filter(Boolean);
        let current: Record<string, unknown> = obj;

        for (let i = 0; i < keys.length - 1; i++) {
            const key = keys[i];
            if (!(key in current)) {
                current[key] = /^\d+$/.test(keys[i + 1]) ? [] : {};
            }
            current = current[key] as Record<string, unknown>;
        }

        current[keys[keys.length - 1]] = value;
    }, []);

    /**
     * Upload all files found in form data and replace with S3 URLs
     */
    const uploadAllFiles = useCallback(
        async <T extends Record<string, unknown>>(formData: T): Promise<T> => {
            setIsUploading(true);
            try {
                const fileTasks = extractFileObjects(formData);

                if (fileTasks.length === 0) {
                    return formData;
                }

                const uploadResults: Array<{ path: string; url: string }> = [];
                const failedUploads: Array<{ path: string; error: string }> = [];

                for (const task of fileTasks) {
                    /**
                     * API 需要加上prefix
                     * - image: 圖片檔案
                     * - video: 影片檔案
                     * - audio: 音訊檔案
                     *
                     * 根據檔案路徑推斷 prefix
                     * - audioFile_0_0_0: audio
                     * - mediaFile_0: image
                     * - mediaFile_1: video
                     * - mediaFile_2: image
                     */

                    try {
                        let prefix: FilePrefix = "image"; // 預設值

                        if (task.path.startsWith("audioFile_")) {
                            prefix = "audio";
                        } else if (task.path.startsWith("mediaFile_")) {
                            const indexStr = task.path.replace("mediaFile_", "");
                            const mediaIndex = parseInt(indexStr, 10);

                            if (!isNaN(mediaIndex)) {
                                const media = (formData as { media?: Array<{ type?: string }> }).media;
                                if (media && media[mediaIndex]) {
                                    const mediaType = media[mediaIndex].type;
                                    prefix = mediaType === "video" ? "video" : "image";
                                }
                            }
                        } else if (task.path.includes("bgmFile")) {
                            // 遊戲設定的背景音樂檔案 (games.puzzle.bgmFile, games.quiz.bgmFile, etc.)
                            prefix = "audio";
                        } else if (task.path.includes("bgFile")) {
                            // 遊戲設定的背景圖片檔案 (games.puzzle.bgFile, games.quiz.bgFile, etc.)
                            prefix = "image";
                        } else if (task.path.includes("imageFile")) {
                            // 遊戲項目的圖片檔案 (games.puzzle.data[0].content[0].imageFile, etc.)
                            prefix = "image";
                        } else if (task.path.includes("urlFile")) {
                            // 獎品的圖片檔案 (prize.data[0].content[0].urlFile)
                            prefix = "image";
                        } else if (task.path.includes("vrmFile")) {
                            // 角色上傳的 VRM 檔案 (avatar_3d.vrmFile)
                            prefix = "vrm";
                        } else if (task.path.includes("idleVideoFile") || task.path.includes("talkingVideoFile")) {
                            // 角色上傳的 2D 影片檔案 (avatar_2d.idleVideoFile, avatar_2d.talkingVideoFile)
                            prefix = "video";
                        }

                        const result = await uploadFile({ file: task.file, prefix }).unwrap();

                        if (result?.url) {
                            uploadResults.push({ path: task.path, url: result.url });
                        } else {
                            failedUploads.push({
                                path: task.path,
                                error: "No URL returned from upload",
                            });
                        }
                    } catch (error) {
                        failedUploads.push({
                            path: task.path,
                            error: error instanceof Error ? error.message : "Unknown error",
                        });
                    }
                }

                if (failedUploads.length > 0) {
                    const errorMessage = `Failed to upload ${failedUploads.length} file(s): ${failedUploads
                        .map((f) => f.path)
                        .join(", ")}`;
                    customToast.error(errorMessage);
                    throw new Error(errorMessage);
                }

                const updatedData = JSON.parse(JSON.stringify(formData));

                uploadResults.forEach((result) => {
                    let urlPath = result.path;

                    if (urlPath.startsWith("mediaFile_")) {
                        const index = urlPath.replace("mediaFile_", "");
                        urlPath = `media.${index}.url`;
                    } else if (urlPath.startsWith("audioFile_")) {
                        const parts = urlPath.replace("audioFile_", "").split("_");
                        if (parts.length === 3) {
                            urlPath = `greetings.data.${parts[0]}.dialogues.${parts[1]}.sentences.${parts[2]}.audio_url`;
                        }
                    } else if (urlPath.includes("bgmFile")) {
                        // 遊戲設定: games.puzzle.bgmFile -> games.puzzle.bgm
                        urlPath = urlPath.replace("bgmFile", "bgm");
                    } else if (urlPath.includes("bgFile")) {
                        // 遊戲設定: games.puzzle.bgFile -> games.puzzle.bg
                        urlPath = urlPath.replace("bgFile", "bg");
                    } else if (urlPath.includes("imageFile")) {
                        // 遊戲項目: games.puzzle.data[0].content[0].imageFile -> games.puzzle.data[0].content[0].image_url
                        urlPath = urlPath.replace("imageFile", "image_url");
                    } else if (urlPath.includes("urlFile")) {
                        // 獎品: prize.data[0].content[0].urlFile -> prize.data[0].content[0].url
                        urlPath = urlPath.replace("urlFile", "url");
                    } else if (urlPath.includes("vrmFile")) {
                        // 角色上傳: avatar_3d.vrmFile -> avatar_3d.url
                        urlPath = urlPath.replace("vrmFile", "url");
                    } else if (urlPath.includes("idleVideoFile")) {
                        // 角色上傳: avatar_2d.idleVideoFile -> avatar_2d.idle_video_url
                        urlPath = urlPath.replace("idleVideoFile", "idle_video_url");
                    } else if (urlPath.includes("talkingVideoFile")) {
                        // 角色上傳: avatar_2d.talkingVideoFile -> avatar_2d.talking_video_url
                        urlPath = urlPath.replace("talkingVideoFile", "talking_video_url");
                    }

                    setValueByPath(updatedData, urlPath, result.url);
                });

                fileTasks.forEach((task) => {
                    setValueByPath(updatedData, task.path, null);
                });

                return updatedData as T;
            } finally {
                setIsUploading(false);
            }
        },
        [uploadFile, extractFileObjects, setValueByPath]
    );

    return {
        uploadAllFiles,
        isUploading,
    };
};

export default useBatchUploadFiles;
